import React from 'react';
import { useScoringDebug } from '../ScoringDebugContext';
import type { ScoringEngine } from '../ScoringDebugContext';
import styles from '../CommandPalette.module.scss';

/**
 * Debug panel for live-tuning / comparing the two scoring engines.
 *
 * Rendered at the bottom of the CommandPalette dialog when scoring
 * debug mode is enabled. Toggle it with the small button that is
 * always visible at the bottom of the palette.
 */
export const ScoringDebugPanel: React.FC = () => {
  const debug = useScoringDebug();

  return (
    <div className={styles.debugBar}>
      {/* Always-visible toggle row */}
      <div className={styles.debugToggle}>
        <button
          className={styles.debugToggleBtn}
          onClick={debug.toggle}
          title="Toggle scoring debug panel"
        >
          {debug.enabled ? '▾ scoring debug' : '▸ scoring debug'}
        </button>
      </div>

      {/* Expanded controls */}
      {debug.enabled && (
        <div className={styles.debugControls}>
          {/* Engine selector */}
          <div className={styles.debugRow}>
            <span className={styles.debugLabel}>engine</span>
            <div className={styles.debugBtnGroup}>
              {(['manual', 'fuse'] as ScoringEngine[]).map(e => (
                <button
                  key={e}
                  className={`${styles.debugBtn} ${debug.engine === e ? styles.debugBtnActive : ''}`}
                  onClick={() => debug.set('engine', e)}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          {/* Show scores */}
          <div className={styles.debugRow}>
            <span className={styles.debugLabel}>show scores</span>
            <button
              className={`${styles.debugBtn} ${debug.showScores ? styles.debugBtnActive : ''}`}
              onClick={() => debug.set('showScores', !debug.showScores)}
            >
              {debug.showScores ? 'on' : 'off'}
            </button>
          </div>

          {/* Fuse-specific controls */}
          {debug.engine === 'fuse' && (
            <>
              <div className={styles.debugRow}>
                <span className={styles.debugLabel}>
                  threshold <span className={styles.debugValue}>{debug.fuseThreshold.toFixed(2)}</span>
                </span>
                <input
                  className={styles.debugSlider}
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={debug.fuseThreshold}
                  onChange={e => debug.set('fuseThreshold', parseFloat(e.target.value))}
                  title="0 = exact only, 1 = match anything"
                />
              </div>

              <div className={styles.debugRow}>
                <span className={styles.debugLabel}>ignore location</span>
                <button
                  className={`${styles.debugBtn} ${debug.fuseIgnoreLocation ? styles.debugBtnActive : ''}`}
                  onClick={() => debug.set('fuseIgnoreLocation', !debug.fuseIgnoreLocation)}
                >
                  {debug.fuseIgnoreLocation ? 'on' : 'off'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
